"use client";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import CongratulationPopup from "@/components/ui/PopUp/CongratulationPopup";
import RewardPopup from "@/components/ui/PopUp/RewardPopup";
import { OnboardingFlow } from "@/components/onboarding";
import AppLayout from "@/components/layouts/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import Image from "next/image";
import { User, apiService } from "@/services/api";
import { Button, PostSearch, VirtualizedPosts } from "@/components/ui";
import { PostData } from "@/services/api";
import PostCardMenuPopup from "@/components/ui/PostCard/PostCardMenuPopup";
import ErrorBoundary from "@/components/ErrorBoundary";
import { UserActivityService } from "@/services/userActivity";
import { useUnifiedPopup } from "@/components/ui/UnifiedPopup/UnifiedPopup";
import { usePosts } from "@/hooks/usePosts";
import { useCommunityAdminIds } from "@/hooks/useCommunityAdminIds";
import { useCommunities } from "@/contexts/CommunitiesContext";
import { useUpdatePostVisibility } from "@/hooks/useUpdatePostVisibility";
import { useDeletePost } from "@/hooks/useDeletePost";
import CommentPopup from "@/components/ui/Comments/CommentPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { enrichPostsWithCommunity } from "@/utils/enrichPostWithCommunity";
import { parseApiError } from "@/utils/apiErrorHandler";
import { useBlockedUsers } from "@shared/hooks/useBlockedUsers";
import { shouldHidePostForBlock } from "@shared/utils/postVisibility";
import ConfirmDeleteCommunityJoydrop from "@/components/ui/ConfirmDeleteCommunityJoydrop/ConfirmDeleteCommunityJoydrop";
import { getJoydropVisibilityMenuState } from "@/utils/postPrivacy";

const HomePage = () => {
  // URL parameters for post focus and comment popup
  const searchParams = useSearchParams();
  const postId = searchParams.get('post');
  const commentId = searchParams.get('comment');

  // Essential state variables
  const [step, setStep] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { user, token, updateUserDots, refreshUser } = useAuth();
  const getBlockedUsersToken = useCallback(() => token ?? null, [token]);
  const { blockedIds } = useBlockedUsers({ apiService, getToken: getBlockedUsersToken });
  const [selectedFeed, setSelectedFeed] = useState<"For You" | "Highlight">("For You");
  const [hasShown5MinPopup, setHasShown5MinPopup] = useState<boolean>(false);

  // Post focus state
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const [shouldOpenCommentPopup, setShouldOpenCommentPopup] = useState<boolean>(false);
  const { getCommunityPostIds, communityPosts, myCommunities, recommendedCommunities, addCommunityPost, loadCommunityPostsFromApi } = useCommunities();

  // Use posts hook for optimized data management
  const {
    posts,
    loading: postsLoading,
    error: postsError,
    hasMore,
    loadMore,
    refresh: refreshPosts,
    updatePost,
  } = usePosts({
    limit: 10,
    initialOffset: 0,
    onlyHighlights: selectedFeed === "Highlight" ? "highlight" : "",
    autoFetch: true
  });
  // Search state
  const [searchResults, setSearchResults] = useState<PostData[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Community admin IDs for public joydrops (home feed) - show admin icon on sender/receiver and +n hover.
  // Include myCommunities so we fetch admin lists for all joined communities (e.g. when someone made you admin).
  const communityIdsFromPosts = useMemo(() => {
    const fromPosts = searchResults.filter((p) => p.communityId).map((p) => p.communityId!);
    const fromMy = (myCommunities ?? []).map((c) => c.id).filter(Boolean);
    return Array.from(new Set([...fromPosts, ...fromMy]));
  }, [searchResults, myCommunities]);
  const initialAdminIdsFromCommunities = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const c of [...myCommunities, ...recommendedCommunities]) {
      if (c.id) {
        // Only seed with admin roll if explicitly defined
        if (c.userRole === 'admin') map[c.id] = [user?.uid].filter(Boolean) as string[];
        // Note: we don't seed with c.adminId anymore. c.adminId is often a "CreatorId" 
        // that shouldn't grant permissions if the user has stepped down as admin.
      }
    }
    return map;
  }, [myCommunities, recommendedCommunities, user?.uid]);
  const adminIdsByCommunity = useCommunityAdminIds(communityIdsFromPosts, token, initialAdminIdsFromCommunities, user?.uid);
  /** Private joined communities — used to merge sent/received into Home when GET /posts omits them. */
  const privateCommunityIds = useMemo(
    () =>
      new Set(
        myCommunities.filter((c) => !c.isPublic && c.id).map((c) => c.id),
      ),
    [myCommunities],
  );

  /** Main feed plus private-community rows from sent/received APIs (same gap as community page fallback). */
  const [feedPosts, setFeedPosts] = useState<PostData[]>([]);
  /**
   * Fetched in parallel with GET /posts (not after). `null` = in flight or n/a; then array.
   * Avoids waiting for the main feed before private-community rows can appear.
   */
  const [privateCommunityFallback, setPrivateCommunityFallback] = useState<PostData[] | null>(null);

  const prepareHomeFeedPosts = useMemo(
    () => {
      const publicIds = new Set(
        [...myCommunities, ...recommendedCommunities]
          .filter((c) => c.isPublic && c.id)
          .map((c) => c.id),
      );
      return (raw: PostData[]) => {
        const enriched = enrichPostsWithCommunity(raw, [...myCommunities, ...recommendedCommunities]);
        return enriched.map((p) => {
          if (!p.communityId) return p;
          return { ...p, communityIsPublic: publicIds.has(p.communityId) };
        });
      };
    },
    [myCommunities, recommendedCommunities],
  );

  const filterPrivateCommunityPosts = useMemo(
    () => (items: PostData[]) =>
      items.filter((post) => {
        // Non-community posts are always visible on home.
        if (!post.communityId) return true;
        // Community joydrops (public or private community) only belong on the home
        // feed for the sender and the specific people it was sent to — being a
        // member of the community is not enough. Everyone still sees the post on
        // the community page itself; this only governs the home feed.
        const isSender = post.senderID === user?.uid;
        const isDirectReceiver =
          post.receiverID === user?.uid ||
          (Array.isArray(post.receiverIDs) &&
            !!user?.uid &&
            post.receiverIDs.includes(user.uid));
        return isSender || isDirectReceiver;
      }),
    [user?.uid],
  );

  // PostCard menu popup state
  const [menuPopup, setMenuPopup] = useState<{
    isOpen: boolean;
    postId: string | null;
    position: { top: number; left: number } | null;
  }>({
    isOpen: false,
    postId: null,
    position: null
  });

  // Pending delete community joydrop (show confirm before actually deleting)
  const [pendingDeleteCommunity, setPendingDeleteCommunity] = useState<{ postId: string; communityId?: string } | null>(null);
  const [isDeletingCommunityPost, setIsDeletingCommunityPost] = useState(false);

  // Use unified popup system
  const { openPopup, closePopup } = useUnifiedPopup();

  // Use post visibility update hook
  const { updatePostVisibility, loading: updatingVisibility } = useUpdatePostVisibility();

  // Use post deletion hook
  const { deletePost } = useDeletePost();

  // Sent/received for private communities — runs in parallel with usePosts (no `posts` dependency).
  useEffect(() => {
    if (selectedFeed !== "For You" || !user?.uid || !token || privateCommunityIds.size === 0) {
      setPrivateCommunityFallback(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const [sentRes, recRes] = await Promise.all([
          apiService.getSentPosts(user.uid, { limit: 100 }, token),
          apiService.getReceivedPosts(user.uid, { limit: 100 }, token),
        ]);
        if (cancelled) return;

        const sentArr = Array.isArray(sentRes.posts) ? sentRes.posts : [];
        const recArr = Array.isArray(recRes.posts) ? recRes.posts : [];
        const fallback = [...sentArr, ...recArr].filter(
          (p) => p.communityId && privateCommunityIds.has(p.communityId),
        );
        if (!cancelled) setPrivateCommunityFallback(fallback);
      } catch {
        if (!cancelled) setPrivateCommunityFallback([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedFeed, user?.uid, token, privateCommunityIds]);

  const mergedFeedPosts = useMemo(() => {
    const excludeBlocked = (list: PostData[]) =>
      list.filter((p) => !shouldHidePostForBlock(p, blockedIds));
    if (selectedFeed !== "For You") {
      return excludeBlocked(posts);
    }
    if (!user?.uid || !token || privateCommunityIds.size === 0) {
      return excludeBlocked(posts);
    }
    if (privateCommunityFallback === null) {
      return excludeBlocked(posts);
    }

    const byId = new Map<string, PostData>();
    for (const p of posts) {
      if (p.id) byId.set(p.id, p);
    }
    for (const p of privateCommunityFallback) {
      if (p.id && !byId.has(p.id)) {
        byId.set(p.id, p);
      }
    }
    const merged = Array.from(byId.values()).sort((a, b) => {
      const ta = a.createdAt?._seconds ?? 0;
      const tb = b.createdAt?._seconds ?? 0;
      return tb - ta;
    });
    return excludeBlocked(merged);
  }, [
    posts,
    selectedFeed,
    user?.uid,
    token,
    privateCommunityIds,
    privateCommunityFallback,
    blockedIds,
  ]);

  // Apply merged list to local state + privacy pipeline (sync only — no network).
  useEffect(() => {
    setFeedPosts(mergedFeedPosts);
    setSearchResults(
      filterPrivateCommunityPosts(prepareHomeFeedPosts(mergedFeedPosts)),
    );
  }, [mergedFeedPosts, filterPrivateCommunityPosts, prepareHomeFeedPosts]);

  // Handle URL parameters for post focus and comment popup
  useEffect(() => {
    if (postId) {
      setFocusedPostId(postId);
      if (commentId) {
        setShouldOpenCommentPopup(true);
      }
    }
  }, [postId, commentId]);

  // Handle post focus and comment popup opening
  useEffect(() => {
    if (focusedPostId && searchResults.length > 0) {
      // Find the focused post in search results
      const focusedPost = searchResults.find(post => post.id === focusedPostId);
      if (focusedPost) {
        // Scroll to the post after a short delay to ensure it's rendered
        setTimeout(() => {
          const postElement = document.getElementById(`post-${focusedPostId}`);
          if (postElement) {
            postElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });

            // Add highlight effect
            postElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');

            // Remove highlight after 3 seconds
            setTimeout(() => {
              postElement.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
            }, 3000);
          }
        }, 500);
      }
    }
  }, [focusedPostId, searchResults]);

  // Handle comment popup opening
  useEffect(() => {
    if (shouldOpenCommentPopup && focusedPostId) {
      // Open comment popup for the focused post using the same pattern as PostCard
      openPopup({
        id: `comments-${focusedPostId}`,
        type: "custom",
        position: "center",
        size: "md",
        props: {
          content: (
            <CommentPopup
              popupId={`comments-${focusedPostId}`}
              postId={focusedPostId}
              communityId={[...feedPosts, ...searchResults].find((p) => p.id === focusedPostId)?.communityId ?? null}
              onSubmitted={() => {
                // Optional: refresh posts or update comment count
                console.log('Comment submitted successfully');
              }}
              onClose={() => {
                // Optional: add any cleanup if needed
                closePopup(`comments-${focusedPostId}`);
              }}
            />
          ),
        },
      });

      // Reset the flag
      setShouldOpenCommentPopup(false);
    }
  }, [shouldOpenCommentPopup, focusedPostId, openPopup, closePopup, feedPosts, searchResults]);

  useEffect(() => {
    refreshPosts();
  }, [selectedFeed]);

  useEffect(() => {
    if (!user || !token) return;

    // Initialize data fetching
    const initializeData = async () => {
      try {
        // Track login activity using the API
        const loginResponse = await UserActivityService.recordLogin(user.uid, token);

        // Check if user should see onboarding (first login)
        const onboardingStatus = UserActivityService.shouldShowOnboarding(loginResponse);
        // Check if user should see reward popup (third day login)
        if (onboardingStatus.showOnboarding) {
          // First time user - show onboarding
          setStep(0);
          return;
        }

        const rewardStatus = UserActivityService.shouldShowRewardPopup(loginResponse);

        // Only show reward popups if showReward is explicitly true
        if (rewardStatus.showReward === true && !onboardingStatus.showOnboarding) {
          // Third day login with reward - show reward popups
          setStep(1); // Start with CongratulationPopup 
        } else {
          // Don't show any popup initially - wait for 5 minutes
          setStep(null);
        }

        // Update user dots if any were awarded
        if (loginResponse.data.dotsAwarded > 0) {
          console.log(`Awarded ${loginResponse.data.dotsAwarded} dots for login activity`);
          // Refresh user data to get the new total from the server (backend already added them)
          refreshUser();
        }

        // Points are now fetched from AuthContext automatically
        // Posts are now fetched automatically by the usePosts hook

      } catch (error) {
        const parsed = parseApiError(error);
        if (parsed.type === "network") {
          console.debug("Home init: API unreachable; skipping login tracking popups.");
        } else {
          console.error("Error initializing data:", error);
        }
        // Default to no popup if there's an error - let 5-minute timer handle it
        setStep(null);
      }
    };

    initializeData();
  }, [user, token]);

  // 5-minute timer effect - show popup only once per day if there's an active sprint
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Check if we've already shown the popup today
      const today = new Date().toDateString();
      const lastShownDate = localStorage.getItem('homePopupShownDate');

      if (!hasShown5MinPopup && step === null && lastShownDate !== today && user && token) {
        try {
          // Check if there's an active sprint
          const sprintResponse: any = await apiService.getCurrentSprint(token);

          // Only show RewardPopup if there's an active sprint
          if (sprintResponse.statusCode === 200 && sprintResponse.data) {
            setStep(2);
            setHasShown5MinPopup(true);
            // Store today's date to prevent showing again today
            localStorage.setItem('homePopupShownDate', today);
          }
        } catch (error) {
          // No active sprint found or error - don't show popup
          console.log('No active sprint found, skipping RewardPopup');
        }
      }
    }, 5 * 60 * 1000); // 5 minutes
    return () => clearTimeout(timer);
  }, [hasShown5MinPopup, step, user, token]);



  const handleNext = () => {
    if (step === 0) {
      setStep(3);
      return;
    }
    if (step === null) return;
    setStep(3);
  };

  // The onboarding quiz (also used pre-login at /joydrop-moments) doesn't
  // know about the server-side onboarding flag or how to dismiss itself
  // inside /home — replicate what the old carousel's "JOYDROP IT" button
  // did: mark onboarding complete, then fall through to handleNext.
  const handleOnboardingQuizComplete = async () => {
    if (user?.uid && token) {
      try {
        await apiService.completeOnboarding(user.uid, token);
      } catch (e) {
        console.error("Failed to complete onboarding:", e);
      }
    }
    handleNext();
  };


  const handleJoydropClick = () => {
    openPopup({
      id: 'sendJoydrop',
      type: 'sendJoydrop',
      position: 'center',
      size: '2xl',
      props: {
        receiver: selectedUser,
        refreshPosts: refreshPosts,
      },
      onSuccess: () => {
        console.log('Joydrop sent successfully');
        refreshPosts(); // Refresh posts to show the new one
      },
      onError: (error) => {
        console.error('Failed to send joydrop:', error);
      },
    });
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  // Handle post search selection
  const handlePostSelect = (post: PostData) => {
    console.log('Selected post:', post);
    // You can add navigation to the specific post or other actions here
  };

  // Handle search results update (exclude community posts)
  const handleSearchResults = (results: PostData[]) => {
    setSearchResults(filterPrivateCommunityPosts(prepareHomeFeedPosts(results)));
    setIsSearching(false);
  };

  // Handle search start
  const handleSearchStart = () => {
    setIsSearching(true);
  };

  // Handle PostCard menu popup
  const handleMenuClick = (postId: string, buttonRect: DOMRect) => {
    const position = {
      top: buttonRect.bottom + 8, // Position below the button
      left: Math.min(buttonRect.right - 20, window.innerWidth - 220) // Align to right edge, with margin
    };

    setMenuPopup({
      isOpen: true,
      postId,
      position
    });
  };

  const handleCloseMenu = () => {
    setMenuPopup({
      isOpen: false,
      postId: null,
      position: null
    });
  };

  const handleMakePrivate = async () => {
    if (!menuPopup.postId) return;

    console.log('Making post private:', menuPopup.postId);

    try {
      const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
      if (!post) {
        console.error('Post not found');
        return;
      }

      // Toggle visibility (if currently public, make private; if private, make public)
      const newVisibility = !post.isPublic;

      await updatePostVisibility({
        postId: menuPopup.postId,
        isPublic: newVisibility,
      });

      // Update the post in the local state
      updatePost(menuPopup.postId, { isPublic: newVisibility });

      handleCloseMenu();
    } catch (error) {
      handleCloseMenu();
      console.error('Failed to update post visibility:', error);
    }
  };

  const handleDeletePost = async () => {
    if (!menuPopup.postId) return;

    try {
      const post = [...feedPosts, ...searchResults].find(p => p.id === menuPopup.postId);
      const communityId = post?.communityId;

      const success = await deletePost(menuPopup.postId, communityId);

      if (success) {
        // Update local state by filtering out the deleted post
        setSearchResults(prev => prev.filter(p => p.id !== menuPopup.postId));
        updatePost(menuPopup.postId, { id: 'deleted' } as any); // Mark as deleted in hook state
      }

      handleCloseMenu();
    } catch (error) {
      handleCloseMenu();
      console.error('Failed to delete post:', error);
    }
  };

  const handleRequestDeleteCommunityPost = () => {
    const post = [...feedPosts, ...searchResults].find(p => p.id === menuPopup.postId);
    if (!post?.id) return;
    setPendingDeleteCommunity({ postId: post.id, communityId: post.communityId });
    handleCloseMenu();
  };

  const handleConfirmDeleteCommunityPost = async () => {
    if (!pendingDeleteCommunity) return;
    setIsDeletingCommunityPost(true);
    try {
      const success = await deletePost(pendingDeleteCommunity.postId, pendingDeleteCommunity.communityId);
      if (success) {
        setSearchResults(prev => prev.filter(p => p.id !== pendingDeleteCommunity.postId));
        updatePost(pendingDeleteCommunity.postId, { id: 'deleted' } as any);
      }
      setPendingDeleteCommunity(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
    } finally {
      setIsDeletingCommunityPost(false);
    }
  };

  const handleEdit = () => {
    const post = [...feedPosts, ...searchResults].find(p => p.id === menuPopup.postId);
    if (!post) return;
    handleCloseMenu();
    openPopup({
      id: 'editJoydrop',
      type: 'sendJoydrop',
      position: 'center',
      size: '2xl',
      props: {
        receiver: null,
        editingPost: post,
        refreshPosts,
      },
      onSuccess: () => {
        refreshPosts();
      },
      onError: (error) => {
        console.error('Failed to edit joydrop:', error);
      },
    });
  };

  const handleReport = () => {
    // Close menu immediately when report is clicked
    handleCloseMenu();

    // Find the post to get ownerId
    const post = [...feedPosts, ...searchResults].find(p => p.id === menuPopup.postId);
    const targetOwnerId = post?.senderID || '';
    const targetId = menuPopup.postId;

    if (!targetId || !targetOwnerId) {
      console.error('Missing data to submit report');
      return;
    }


    // Open report popup
    openPopup({
      id: 'reportPost',
      type: 'reportPost',
      position: 'center',
      props: {
        reportType: 'joydrop',
        targetId,
        targetOwnerId,
        currentUserId: user?.uid,
      },
      onSuccess: () => {
        console.log('Report post successfully');
      },
      onError: (error) => {
        console.error('Failed to report post:', error);
      },
    });
  };

  const handleBlockFromMenu = () => {
    handleCloseMenu();
    const post = [...feedPosts, ...searchResults].find(p => p.id === menuPopup.postId);
    if (!post?.senderID) return;
    openPopup({
      id: `block-user-${post.senderID}`,
      type: 'blockUser',
      position: 'center',
      props: {
        targetUserId: post.senderID,
        targetUsername: post.sender?.username,
        targetDisplayName: post.sender?.name,
      },
    });
  };

  const handleReportCommunityPost = () => {
    const postId = menuPopup.postId;
    handleCloseMenu();
    const post = [...feedPosts, ...searchResults].find((p) => p.id === postId);
    if (!post?.id || !post?.senderID) return;
    openPopup({
      id: "reportCommunityPost",
      type: "reportPost",
      position: "center",
      props: {
        reportType: "joydrop",
        targetId: post.id,
        targetOwnerId: post.senderID,
        currentUserId: user?.uid,
      },
    });
  };

  const handleEditCommunityPost = () => {
    const postId = menuPopup.postId;
    handleCloseMenu();
    const post = [...feedPosts, ...searchResults].find((p) => p.id === postId);
    if (!post?.id || !post?.communityId) return;
    const community = myCommunities.find((c) => c.id === post.communityId) ?? recommendedCommunities.find((c) => c.id === post.communityId);
    if (!community) return;
    openPopup({
      id: "editCommunityJoydrop",
      type: "sendCommunityJoydrop",
      position: "center",
      size: "2xl",
      props: {
        community,
        editingPost: post,
        refreshPosts: async () => {
          refreshPosts();
          if (post.communityId) {
            await loadCommunityPostsFromApi(post.communityId, { isPrivate: !community.isPublic });
          }
        },
        onCommunityPostCreated: (cid: string, p: PostData) => {
          addCommunityPost(cid, p);
          loadCommunityPostsFromApi(cid, { isPrivate: !community.isPublic });
        },
      },
      onSuccess: async () => {
        refreshPosts();
        if (post.communityId) {
          await loadCommunityPostsFromApi(post.communityId, { isPrivate: !community.isPublic });
        }
      },
    });
  };


  return (
    <>
      {step == 0 && (
        <div className="fixed inset-0 z-[100] flex min-h-dvh w-full flex-col overflow-hidden">
          <OnboardingFlow onComplete={handleOnboardingQuizComplete} />
        </div>
      )}
      {step === 1 && <CongratulationPopup popupType="joiningBonus" balance={500} onBack={handleNext} />}
      {step === 2 && <RewardPopup onClose={handleNext} />}

      {step !== 0 && (
        <div className="bg-[#F9F4F2] min-h-screen">
          <AppLayout
            maxWidth="max-w-[620px]"
            className="flex flex-col"
            showFeedFilter={true}
            selectedFeed={selectedFeed}
            onFeedChange={setSelectedFeed}
          >
            {/* Search / Joydrop Input */}

            <div className="flex flex-row gap-3 items-center w-full max-w-[620px] mb-8">
              <div className="relative flex-1">
                <PostSearch
                  onSelect={handlePostSelect}
                  onSearchResults={handleSearchResults}
                  onSearchStart={handleSearchStart}
                  setSearchResults={setSearchResults}
                  placeholder="Search posts..."
                  className="space-mono-text block w-full h-[46px] pl-10 pr-3 border-asymmetric border-[#151515] rounded-lg text-[#6B7280] bg-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#6c2bff] focus:border-[#6c2bff] leading-none"
                  userID={user?.uid}
                  limit={20}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <FontAwesomeIcon icon={faSearch} className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => handleJoydropClick()}
                className="flex items-center justify-center md:justify-between h-[46px] whitespace-nowrap !rounded-lg border-[1.5px] border-gray-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] w-[46px] md:w-[155px] px-0 md:px-4 bg-[#6c2bff] text-white hover:bg-[#5A22D9] transition-colors gap-2"
              >
                <div className=" md:ml-0 flex items-center h-full">
                  <Image
                    src="/send-jd.svg"
                    alt="Send joydrop"
                    width={16}
                    height={16}
                    className="w-4 h-4 brightness-0 invert"
                  />
                </div>
                <span className="hidden md:inline font-bold font-montserrat text-xs">Send joydrop</span>
              </button>
            </div>

            {/* Posts Feed */}
            <div className="flex flex-col gap-6">
              {isSearching ? (
                // Search Loading State
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="mb-6">
                    <div className="w-8 h-8 border-4 border-[#1D67CD] border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                  <h2 className="fraunces-display text-2xl sm:text-3xl text-black mb-3">
                    Searching...
                  </h2>
                  <p className="space-mono-text text-base sm:text-lg text-black mb-8 max-w-md">
                    Finding posts that match your search.
                  </p>
                </div>
              ) : searchResults?.length === 0 ? (
                // Empty State UI - matches the image exactly
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  {/* Popcorn Illustration */}
                  <div className="mb-6">
                    <Image
                      src="/notoEmoji/popcorn.svg"
                      alt="Popcorn illustration"
                      width={120}
                      height={120}
                      className="w-24 h-24 sm:w-32 sm:h-32"
                    />
                  </div>

                  {/* Headline */}
                  <h2 className="fraunces-display text-2xl sm:text-3xl text-black mb-3">
                    Start the fun!
                  </h2>

                  {/* Description */}
                  <p className="space-mono-text text-base sm:text-lg text-black mb-8 max-w-md">
                    No joydrops in your feed yet — send one and light it up!
                  </p>

                  {/* Call to Action Button */}
                  <button
                    onClick={handleJoydropClick}
                    className="space-mono-text px-8 py-3 bg-[#6c2bff] text-white rounded-lg hover:bg-[#5A22D9] transition-colors flex items-center justify-center gap-2"
                    style={{
                      borderTop: "1px solid #151515",
                      borderRight: "1px solid #151515",
                      borderBottom: "1px solid #151515",
                      borderLeft: "1px solid #151515",
                    }}
                  >
                    <Image
                      src="/send-jd.svg"
                      alt=""
                      width={20}
                      height={20}
                      className="brightness-0 invert"
                    />
                    Send joydrop
                  </button>
                </div>
              ) : (
                <ErrorBoundary>
                  <VirtualizedPosts
                    posts={searchResults}
                    loading={postsLoading || isSearching}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    onRefresh={refreshPosts}
                    className="h-full"
                    onMenuClick={handleMenuClick}
                    isMenuOpen={menuPopup.isOpen}
                    onCloseMenu={() => { }}
                    selectedFeed={selectedFeed === "For You" ? "for you" : "Highlight"}
                    focusedPostId={focusedPostId}
                    showCommunityHeader={true}
                    communityAdminIdsByCommunity={adminIdsByCommunity}
                  />
                </ErrorBoundary>
              )}
            </div>

            <PostCardMenuPopup
              isOpen={menuPopup.isOpen}
              position={menuPopup.position}
              onClose={handleCloseMenu}
              onMakePrivate={handleMakePrivate}
              onReport={handleReport}
              onBlock={handleBlockFromMenu}
              onEdit={handleEdit}
              isPublic={(() => {
                const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
                return post?.isPublic ?? true;
              })()}
              visibilityMenu={(() => {
                const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
                return getJoydropVisibilityMenuState(post, user?.uid);
              })()}
              loading={updatingVisibility}
              currentUserId={user?.uid}
              postSenderId={(() => {
                const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
                return post?.senderID;
              })()}
              isCommunityPost={(() => {
                const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
                return !!post?.communityId;
              })()}
              isCommunityAdmin={(() => {
                const post = [...feedPosts, ...searchResults].find((p) => p.id === menuPopup.postId);
                if (!post?.communityId || !user?.uid) return false;
                return adminIdsByCommunity[post.communityId]?.includes(user.uid) ?? false;
              })()}
              onDeleteCommunityPost={handleRequestDeleteCommunityPost}
              onReportCommunityPost={handleReportCommunityPost}
              onEditCommunityPost={handleEditCommunityPost}
            />

            <ConfirmDeleteCommunityJoydrop
              isOpen={!!pendingDeleteCommunity}
              onClose={() => setPendingDeleteCommunity(null)}
              onConfirm={handleConfirmDeleteCommunityPost}
              isDeleting={isDeletingCommunityPost}
            />
          </AppLayout>
        </div>
      )}
    </>
  );
};

export default HomePage;
